import { AlertTriangle, ArrowRight, MapPin, ShieldCheck, Users } from "lucide-react";
import { Layout } from "../components/Layout";
import { REGISTER_URL } from "../data/content";
import { guidesBySlug } from "../data/guides";

const RELATED_GUIDE_SLUGS = [
  "giftkoeder-erkennen-und-melden",
  "hund-entlaufen-erste-schritte",
  "katze-entlaufen-was-tun",
] as const;

const REPORT_TYPES = [
  "Giftköder",
  "Fallen",
  "aggressive Hunde",
  "Straßengefahren",
  "vermisste Tiere",
] as const;

/** Dedicated, indexable feature page for the Warnkarte (route "/funktionen/warnkarte"). */
export function Warnkarte(): JSX.Element {
  const relatedGuides = RELATED_GUIDE_SLUGS.map((slug) => guidesBySlug[slug]).filter(
    (g): g is NonNullable<typeof g> => Boolean(g),
  );

  return (
    <Layout>
      <section className="border-b border-surface-variant/50 bg-surface-container-low py-16 sm:py-20 lg:py-24">
        <div className="container-welope">
          <nav aria-label="Brotkrumen" className="mb-6">
            <ol className="flex flex-wrap items-center gap-2 font-body text-label-sm text-on-surface-variant">
              <li>
                <a href="/" className="transition-colors hover:text-primary">
                  Start
                </a>
              </li>
              <li aria-hidden>›</li>
              <li>
                <a href="/#funktionen" className="transition-colors hover:text-primary">
                  Funktionen
                </a>
              </li>
              <li aria-hidden>›</li>
              <li className="text-primary">Warnkarte</li>
            </ol>
          </nav>
          <div className="max-w-2xl">
            <p className="section-eyebrow">Funktion</p>
            <h1 className="mt-3 font-headline text-[40px] font-extrabold leading-[1.05] tracking-[-0.02em] text-primary sm:text-[52px]">
              Die Warnkarte
            </h1>
            <p className="mt-5 font-body text-body-lg leading-[1.55] text-on-surface-variant">
              Eine community-getriebene Karte für Gefahren und vermisste Tiere in deiner
              Nähe — von Haltern für Halter, ganz ohne Umwege über Ämter oder Vereine.
            </p>
            <a href={REGISTER_URL} className="btn-primary mt-8 inline-flex">
              Auf die Warteliste
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 lg:py-24">
        <div className="container-welope">
          <div className="mx-auto max-w-2xl">
            <h2 className="font-headline text-headline-lg text-primary">
              Was du auf der Warnkarte markieren kannst
            </h2>
            <p className="mt-4 font-body text-body-md leading-[1.65] text-on-surface-variant">
              Mit einem Tipp auf die Karte markierst du eine Fundstelle oder Gefahr —
              andere Halter in deiner Nähe werden automatisch benachrichtigt, wenn sie
              sich innerhalb ihres selbst gewählten Suchradius befindet.
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {REPORT_TYPES.map((type) => (
                <li
                  key={type}
                  className="flex items-center gap-2 rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-4 py-3 font-body text-body-sm text-on-surface"
                >
                  <AlertTriangle className="h-4 w-4 shrink-0 text-secondary" aria-hidden="true" />
                  {type}
                </li>
              ))}
            </ul>

            <div className="mt-14 grid grid-cols-1 gap-lg sm:grid-cols-3">
              <FeatureFact
                Icon={MapPin}
                title="Wählbarer Suchradius"
                text="Du entscheidest, wie weit Warnungen zu dir reichen — von der eigenen Straße bis zum ganzen Stadtteil."
              />
              <FeatureFact
                Icon={Users}
                title="Community-getrieben"
                text="Jede Meldung kommt von echten Haltern in deiner Nähe, nicht von einer zentralen Redaktion."
              />
              <FeatureFact
                Icon={ShieldCheck}
                title="Opt-in, kein Zwang"
                text="Die Warnkarte nutzt deinen Standort nur, wenn du aktiv zustimmst — ganz ohne Dark Patterns."
              />
            </div>
          </div>
        </div>
      </section>

      {relatedGuides.length > 0 && (
        <section className="border-t border-surface-variant/50 bg-surface-container py-16 sm:py-20">
          <div className="container-welope">
            <div className="mx-auto max-w-2xl">
              <h2 className="font-headline text-headline-md text-primary">
                Passend dazu im Ratgeber
              </h2>
              <ul className="mt-6 space-y-3">
                {relatedGuides.map((guide) => (
                  <li key={guide.slug}>
                    <a
                      href={`/ratgeber/${guide.slug}`}
                      className="group flex items-center justify-between gap-4 rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-md py-4 transition-colors hover:border-primary/40"
                    >
                      <span className="font-body text-body-md font-medium text-on-surface">
                        {guide.title}
                      </span>
                      <ArrowRight
                        className="h-4 w-4 shrink-0 text-primary transition-transform group-hover:translate-x-0.5"
                        aria-hidden="true"
                      />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}

function FeatureFact({
  Icon,
  title,
  text,
}: {
  Icon: typeof MapPin;
  title: string;
  text: string;
}): JSX.Element {
  return (
    <div>
      <Icon className="h-6 w-6 text-primary" strokeWidth={1.6} aria-hidden="true" />
      <h3 className="mt-3 font-headline text-headline-md text-on-surface">{title}</h3>
      <p className="mt-1.5 font-body text-body-sm leading-[1.55] text-on-surface-variant">
        {text}
      </p>
    </div>
  );
}
